/**
 * ActionMenu Component
 * 
 * A dropdown menu for row-level actions in tables.
 * Click the trigger button to open menu, click outside to close.
 */

import React, { useState, useRef, useEffect } from 'react';
import './ActionMenu.scss';

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  icon?: string;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  triggerLabel?: string;
  triggerIcon?: string;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  triggerLabel,
  triggerIcon = '⋮',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [positionUp, setPositionUp] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position based on available space
  useEffect(() => {
    if (isOpen && menuRef.current && dropdownRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      // Position upward if there's not enough space below but enough above
      if (spaceBelow < dropdownHeight + 10 && spaceAbove > dropdownHeight + 10) {
        setPositionUp(true);
      } else {
        setPositionUp(false);
      }
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleItemClick = (item: ActionMenuItem) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.disabled) {
      item.onClick();
      setIsOpen(false);
    }
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className="action-menu" ref={menuRef}>
      <button
        className="action-menu-trigger"
        onClick={handleTriggerClick}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {triggerLabel && <span className="trigger-label">{triggerLabel}</span>}
        <span className="trigger-icon">{triggerIcon}</span>
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef}
          className={`action-menu-dropdown ${positionUp ? 'position-up' : ''}`}
        >
          {items.map((item, index) => (
            <button
              key={index}
              className={`action-menu-item ${item.variant || ''} ${item.disabled ? 'disabled' : ''}`}
              onClick={handleItemClick(item)}
              disabled={item.disabled}
            >
              {item.icon && <span className="item-icon">{item.icon}</span>}
              <span className="item-label">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionMenu;
